import React, { ReactNode, useState, useEffect } from "react";
import { ConsentSettings, ConsentContext } from "../../Context/ConsentContext";
import ConfigService from "../../Core/ConfigService";
import ReactGA from 'react-ga4';

const COOKIE_CONSENT_KEY = 'sudoku_vs_cookie_consent';

// Default settings applied immediately on page load (granted)
const defaultConsent: ConsentSettings = {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
};

export const ConsentContextProvider = ({ children }: { children: ReactNode }) => {
    // Main consent state that will be used for actual consent management
    const [consent, setConsent] = useState<ConsentSettings | null>(null);

    // UI states
    const [showBanner, setShowBanner] = useState<boolean>(false);
    const [showCustomizeSettings, setShowCustomizeSettings] = useState<boolean>(false);
    const [analyticsDetails, setAnalyticsDetails] = useState<boolean>(false);
    const [advertisingDetails, setAdvertisingDetails] = useState<boolean>(false);

    // State for what appears in the banner (can be modified before confirming)
    const [partialConsent, setPartialConsent] = useState<ConsentSettings>({ ...defaultConsent });

    // Push updated consent when user confirms or stored settings
    const pushUpdatedConsent = (settings: ConsentSettings) => {
        window.gtag('consent', 'update', settings);
    };

    // Initialize GA
    const initializeAnalytics = async () => {
        try {
            const config = await ConfigService.get();
            ReactGA.initialize(config.Analytics.TrackingId, {
                gaOptions: { cookieFlags: 'max-age=7200;secure;samesite=none' }
            });
        } catch (error) {
            console.error('Failed to initialize analytics:', error);
        }
    };

    // Toggle individual consent settings in the banner
    const toggleConsentSetting = (setting: keyof ConsentSettings) => {
        setPartialConsent(prev => ({
            ...prev,
            [setting]: prev[setting] === 'granted' ? 'denied' : 'granted'
        }));
    };

    // Toggle between landing and customize settings
    const toggleCustomizeSettings = () => {
        setShowCustomizeSettings(prev => !prev);
    };

    // Confirm consent with current partial settings
    const confirmConsent = async () => {
        const newSettings: ConsentSettings = { ...partialConsent };

        // Save to localStorage without timestamp
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newSettings));

        // Update consent in GTM
        pushUpdatedConsent(newSettings);

        // Only initialize/reinitialize analytics if consent changed
        if (newSettings.analytics_storage === 'granted') {
            await initializeAnalytics();
        }

        // Update state
        setConsent(newSettings);
        setShowBanner(false);
        setShowCustomizeSettings(false);
    };

    // Toggle details UI
    const toggleConsentDetail = (type: 'analytics' | 'advertising') => {
        if (type === 'analytics') setAnalyticsDetails(prev => !prev);
        else setAdvertisingDetails(prev => !prev);
    };

    // On mount: check for stored settings, if none, show the banner
    useEffect(() => {
        // Check for stored preferences
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (stored) {
            try {
                // User has previously set preferences
                const parsedSettings: ConsentSettings = JSON.parse(stored);

                // Update UI state with stored preferences
                setPartialConsent(parsedSettings);
                setConsent(parsedSettings);

                // Don't show banner if user has previously confirmed
                setShowBanner(false);
            } catch (error) {
                // Invalid stored settings, show banner with defaults
                setConsent(defaultConsent);
                setShowBanner(true);
            }
        } else {
            // No stored preferences, set default and show banner
            setConsent(defaultConsent);
            setShowBanner(true);
        }

        // Initialize analytics if needed (based on consent-init.ts already applying consent)
        if (!stored || (stored && JSON.parse(stored).analytics_storage === 'granted')) {
            initializeAnalytics();
        }
    }, []);

    return (
        <ConsentContext.Provider
            value={{
                consent,
                confirmConsent,
                hasConsented: !!consent && consent.analytics_storage === 'granted',
                showBanner,
                showCustomizeSettings,
                toggleCustomizeSettings,
                toggleConsentDetail,
                analyticsDetails,
                advertisingDetails,
                partialConsent,
                toggleConsentSetting
            }}
        >
            {children}
        </ConsentContext.Provider>
    );
};